/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Sunday, May 8th 2022, 2:29:03 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import { APISERVICEPATHS, APIMakeRequestFncArgs } from "lib/api";
import { ListEditorProvider, useListEditorContext, IListEditorContext } from "lib/providers";
import { TaskTemplate, TASK_DEFINITION_UPDATE_ACTIONS } from "lib/schemas";
import React from "react";
import { useServiceBuilderContext } from "service-builder/providers";
import { useAPIReorderTaskTemplate, useAPIMutateTaskTemplate } from "service-builder/services";
import { FlowChartProvider } from "./FlowChartProvider";

export interface IWorkflowContext {
  taskTemplateListContext: IListEditorContext<TaskTemplate>;
  saveWorkflowStep: (sourceId: TaskTemplate["id"], targetId: TaskTemplate["id"]) => void;
  deleteWorkflowEdge: (sourceId: TaskTemplate["id"], targetId: TaskTemplate["id"]) => void;
  patchTaskTemplate: (args: APIMakeRequestFncArgs<TaskTemplate>) => void;
}

export const WorkflowProvider = ({ children }) => {
  const { processDefinitionContext } = useServiceBuilderContext();
  const { currentProcessDefinition } = processDefinitionContext;

  return currentProcessDefinition?.id ? (
    <ListEditorProvider<TaskTemplate, true>
      uniqueIdentifier="id"
      positionLabel="sequence"
      useAPIReorderListItem={useAPIReorderTaskTemplate}
      apiServicePath={APISERVICEPATHS.taskDefinitions}
      apiQueryParams={{ process_definition: currentProcessDefinition.id }}
      dependencyList={[currentProcessDefinition.id]}
    >
      <FlowChartProvider>{children}</FlowChartProvider>
    </ListEditorProvider>
  ) : null;
};

export const useWorkflowContext = (): IWorkflowContext => {
  const taskTemplateListContext = useListEditorContext<TaskTemplate>();

  const { findItem, updateList } = taskTemplateListContext;

  const { patchData: patchTaskTemplate } = useAPIMutateTaskTemplate();

  const addOutgoingBranch = (source: TaskTemplate, target: TaskTemplate) => {
    // TODO: Implement a function that creates default activation configuration on the backend.
    // This is low priority since our ux for now involves the user having to fill in a pop up
    // form for the configuration.
  };

  const addNext = (source: TaskTemplate, target: TaskTemplate) => {
    patchTaskTemplate({
      apiPathParams: [source.id],
      body: JSON.stringify({ next: target.id }),
      setValidAPIResponse: (apiResponse) => updateList(apiResponse),
    });
  };

  const saveWorkflowStep = React.useCallback<IWorkflowContext["saveWorkflowStep"]>(
    (sourceId, targetId) => {
      const sourceTaskTemplate = findItem(sourceId)?.item;
      const targetTaskTemplate = findItem(targetId)?.item;

      if (!sourceTaskTemplate || !targetTaskTemplate) return;

      switch (sourceTaskTemplate?.type) {
        case "Split":
          addOutgoingBranch(sourceTaskTemplate, targetTaskTemplate);
          break;
        default:
          addNext(sourceTaskTemplate, targetTaskTemplate);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [findItem]
  );

  const deleteWorkflowEdge = React.useCallback<IWorkflowContext["deleteWorkflowEdge"]>(
    (sourceId, targetId) => {
      const sourceTaskTemplate = findItem(sourceId)?.item;
      const targetTaskTemplate = findItem(targetId)?.item;

      if (!sourceTaskTemplate || !targetTaskTemplate) return;

      patchTaskTemplate({
        apiPathParams: [sourceTaskTemplate.id],
        body: JSON.stringify({
          update_action: TASK_DEFINITION_UPDATE_ACTIONS.delete_outgoing_branch,
          outgoing_branch_to_delete: targetTaskTemplate.id,
        }),
        setValidAPIResponse: (apiResponse) => updateList(apiResponse),
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [findItem]
  );

  return {
    taskTemplateListContext,
    saveWorkflowStep,
    patchTaskTemplate,
    deleteWorkflowEdge,
  };
};
